import { HttpWidgetService } from "../Services/http.clients/http.widget.client";
import { useRouterStore } from "../Stores/Router";
import { useSessionStore } from "../Stores/Session";
import CommunicationManager from "./CommunicationManager";
import { fillSessionPlaceholders, isOpenableUrl } from "./MessageTrust";

export default class Communicator {
  /**
   * Switches the widget to another contact's session after a verified email
   * moved the conversation there. Hosts that can (JS SDK 19.4+) store it, so
   * it survives a reload; older hosts get the switch for this page view only.
   */
  static adoptSession(sessionData: any, shareToken?: string) {
    if (!sessionData?.gleapId || !sessionData?.gleapHash) return;
    const manager = CommunicationManager.getInstance();
    manager.pendingAdoption = { gleapId: sessionData.gleapId, shareToken };
    if (manager.hostCanAdoptSession) {
      manager.sendMessage({
        name: "adopt-session",
        data: { gleapId: sessionData.gleapId, gleapHash: sessionData.gleapHash },
      });
      return;
    }
    const { apiUrl, sdkKey } = useSessionStore.getState();
    manager.applySessionUpdate({ sessionData, sdkKey, apiUrl });
  }

  /**
   * Asks the host to open a link. Script-capable schemes (javascript:, data:,
   * …) are dropped. `{{session.<field>}}` placeholders are filled only for
   * links the operator configured (menu items, checklist / survey / content
   * actions) and only with harmless fields (see fillSessionPlaceholders):
   * links that came from messages, Kai, cards or a workflow pass
   * `{ placeholders: false }` and are opened as they are.
   */
  static openURL(
    url: string,
    newTab: boolean = false,
    options: { placeholders?: boolean } = {},
  ) {
    if (typeof url !== "string") {
      return;
    }

    let processedUrl = url;
    if (options.placeholders !== false) {
      try {
        processedUrl = fillSessionPlaceholders(
          url,
          useSessionStore.getState().session,
        );
      } catch (error) {
        processedUrl = url;
      }
    }

    if (!isOpenableUrl(processedUrl)) {
      return;
    }

    CommunicationManager.getInstance().sendMessage({
      name: "open-url",
      data: processedUrl,
      newTab: newTab,
    });
  }

  /**
   * Hands a custom action to the host app (registerCustomAction). `data` is
   * the action name; `actionData` the action's payload, which SDKs from the
   * custom-action-data release pass on as the callback's `data` (older SDKs
   * ignore it).
   */
  static runCustomAction(action: any, shareToken?: string, actionData?: unknown) {
    CommunicationManager.getInstance().sendMessage({
      name: "run-custom-action",
      data: action,
      shareToken,
      ...(actionData !== undefined && actionData !== null ? { actionData } : {}),
    });
  }

  static openImage(url: string) {
    CommunicationManager.getInstance().sendMessage({
      name: "open-image",
      data: {
        url,
      },
    });
  }

  static pageChanged(pageName: string) {
    CommunicationManager.getInstance().sendMessage({
      name: "page-changed",
      data: {
        name: pageName,
      },
    });
  }

  static closeWidget() {
    CommunicationManager.getInstance().sendMessage({
      name: "close-widget",
    });

    useRouterStore.setState({ showBackButton: true });
  }

  static playPing() {
    CommunicationManager.getInstance().sendMessage({
      name: "play-ping",
      data: {},
    });
  }

  // Kai Voice call lifecycle — mirrors video-call-joined/-left so the SDK can
  // show the launcher red-dot and keep the widget open during a call. Old SDK
  // versions ignore unknown message names, so these are safe to send blindly.
  static kaiVoiceStarted() {
    CommunicationManager.getInstance().sendMessage({
      name: "kai-voice-started",
      data: {},
    });
  }

  static kaiVoiceEnded() {
    CommunicationManager.getInstance().sendMessage({
      name: "kai-voice-ended",
      data: {},
    });
  }

  // ── Capture requests (Messenger → host, contract §7) ──
  // Hosts without capture support ignore unknown message names; the card
  // only offers host capture after `capture-capabilities` arrived anyway.
  static captureStart(data: {
    requestId: string;
    kind: "screenshot" | "recording";
    ticketShareToken?: string;
    options: Record<string, any>;
    labels: Record<string, string>;
  }) {
    CommunicationManager.getInstance().sendMessage({
      name: "capture-start",
      data,
    });
  }

  static captureCancel(requestId: string) {
    CommunicationManager.getInstance().sendMessage({
      name: "capture-cancel",
      data: { requestId },
    });
  }

  // The web host shows the widget frame full-viewport while the annotation
  // editor is open; native hosts ignore it.
  static captureEditor(requestId: string, open: boolean) {
    CommunicationManager.getInstance().sendMessage({
      name: "capture-editor",
      data: { requestId, open },
    });
  }

  /**
   * Capture UI v2: the sent result flies from where the customer last saw it
   * into the card's media slot. Only sent to hosts announcing
   * `capture-capabilities.flyIn`; the host answers `capture-fly-done`.
   * `to` is in this frame's client coordinates (the host adds the frame's
   * offset); `from` / `src` come along when the Messenger showed the result
   * itself (the screenshot editor, which covered the viewport). Without them
   * the host flies what it showed last (its recording preview).
   */
  static captureFly(data: {
    requestId: string;
    to: { x: number; y: number; width: number; height: number };
    from?: { x: number; y: number; width: number; height: number };
    src?: string;
    radius?: number;
  }) {
    CommunicationManager.getInstance().sendMessage({
      name: "capture-fly",
      data,
    });
  }

  /**
   * `sent`: whether the host's own capture (its screenshot in the editor) was
   * saved. The host sends that screenshot's logs only then.
   */
  static captureDone(requestId: string, sent: boolean) {
    CommunicationManager.getInstance().sendMessage({
      name: "capture-done",
      data: { requestId, sent },
    });
  }

  static notifyEvent(name: string, data?: any) {
    CommunicationManager.getInstance().sendMessage({
      name: "notify-event",
      data: {
        type: name,
        data: data,
      },
    });
  }

  // Reports a widget-interaction event for analytics straight to the server (no SDK
  // relay needed). Opt-in gating lives in HttpWidgetService; persistence is
  // ClickHouse-only and re-gated server-side.
  static trackWidgetEvent(name: string, data?: any) {
    HttpWidgetService.getInstance().trackEvent(name, data);
  }
}
